import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AlertType, FillerType, Error } from '../types/common';
import {
    CREATE_FILLERTYPE_ERROR_MESSAGE,
    DELETE_FILLERTYPE_ERROR_MESSAGE,
    FETCH_TYPESOFFILLER_ERROR_MESSAGE,
    UPDATE_FILLERTYPE_ERROR_MESSAGE,
} from '../constants/messages';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';

export type FillerTypeState = {
    typesOfFiller: FillerType[];
    selectedFillerType: FillerType;
    isLoading: boolean;
    error: Error;
};

const initialState: FillerTypeState = {
    typesOfFiller: [],
    selectedFillerType: {
        id: '',
        name: '',
        description: '',
        url: '',
    },
    isLoading: false,
    error: {
        isError: false,
        message: '',
    },
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

// Fetch typesOfFiller
export const fetchTypesOfFiller = createAsyncThunk('fillerType/fetchtypesOfFiller', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/typesOfFiller`);

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_TYPESOFFILLER_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_TYPESOFFILLER_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id` для совместимости с компонентом
    const typesOfFiller: FillerType[] = data.map((fillerType: any) => ({
        id: fillerType._id, // Преобразование _id в id
        name: fillerType.name,
        description: fillerType.description,
        url: fillerType.url,
    }));

    return typesOfFiller;
});

// Create fillerType
export const createFillerType = createAsyncThunk('fillerType/createFillerType', async (fillerType: Partial<FillerType>, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFiller`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify(fillerType),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: CREATE_FILLERTYPE_ERROR_MESSAGE }));
        return rejectWithValue(CREATE_FILLERTYPE_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id`
    return { id: data.id, ...fillerType } as FillerType;
});

// Update fillerType
export const updateFillerType = createAsyncThunk('fillerType/updateFillerType', async (fillerType: FillerType, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFiller/${fillerType.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify({
            name: fillerType.name,
            description: fillerType.description,
            url: fillerType.url,
        }),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: UPDATE_FILLERTYPE_ERROR_MESSAGE }));
        return rejectWithValue(UPDATE_FILLERTYPE_ERROR_MESSAGE);
    }

    // Убедимся, что `id` сохраняется
    const data = await response.json();
    const updatedFillerType: FillerType = {
        id: fillerType.id,
        name: data.name ?? fillerType.name,
        description: data.description ?? fillerType.description,
        url: data.url ?? fillerType.url,
    };

    return updatedFillerType;
});

// Delete fillerType
export const deleteFillerType = createAsyncThunk('fillerType/deleteFillerType', async (id: FillerType['id'], { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFiller/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: DELETE_FILLERTYPE_ERROR_MESSAGE }));
        return rejectWithValue(DELETE_FILLERTYPE_ERROR_MESSAGE);
    }

    return id;
});

export const FillerTypeSlice = createSlice({
    name: 'fillerType',
    initialState,
    reducers: {
        selectFillerType: (state, action: PayloadAction<FillerType['id']>) => {
            const fillerType = state.typesOfFiller.find((fillerType) => fillerType.id === action.payload);

            if (!fillerType) return;

            state.selectedFillerType = fillerType;
        },

        removeSelectedFillerType: (state) => {
            state.selectedFillerType = initialState.selectedFillerType;
        },

        resetFillerTypeError: (state) => {},
    },

    extraReducers: (builder) => {
        builder.addCase(fetchTypesOfFiller.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(fetchTypesOfFiller.fulfilled, (state, { payload }) => {
            state.typesOfFiller = payload;
            state.isLoading = false;
        });

        builder.addCase(fetchTypesOfFiller.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(createFillerType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(createFillerType.fulfilled, (state, { payload }) => {
            state.typesOfFiller.push(payload);
            state.isLoading = false;
        });

        builder.addCase(createFillerType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(updateFillerType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(updateFillerType.fulfilled, (state, { payload }) => {
            const index = state.typesOfFiller.findIndex((fillerType) => fillerType.id === payload.id);
            if (index < 0) return;
            state.typesOfFiller[index] = payload;
            state.isLoading = false;
        });

        builder.addCase(updateFillerType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(deleteFillerType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(deleteFillerType.fulfilled, (state, { payload }) => {
            const index = state.typesOfFiller.findIndex((fillerType) => fillerType.id === payload);
            state.typesOfFiller.splice(index, 1);
            state.isLoading = false;
        });

        builder.addCase(deleteFillerType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
    },
});

export const { selectFillerType, removeSelectedFillerType, resetFillerTypeError } = FillerTypeSlice.actions;

export default FillerTypeSlice.reducer;
